import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { IconType } from "@circle-react-shared/Icon";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-uikit/Typography/index";
import { RadioButtonIcon } from "./RadioButtonIcon";

export interface RadioButtonProps extends ComponentPropsWithoutRef<"label"> {
  checkedOptionClassName?: string;
  contentWrapperClassName?: string;
  disabled?: boolean;
  hasCheckedIcon?: boolean;
  hasRadioCheckmarks?: boolean;
  icon?: IconType;
  iconSize?: number;
  iconWrapperClassName?: string;
  id: string;
  image?: ReactNode;
  isChecked: boolean;
  label: ReactNode | string;
  labelClassName?: string;
  name: string;
  onChange: (value: any) => void;
  radioCheckmarksCheckedIcon?: IconType;
  radioCheckmarksUncheckedIcon?: IconType;
  size?: "sm" | "md";
  uncheckedOptionClassName?: string;
  value: any;
}

export const RadioButton = ({
  label,
  name,
  id,
  isChecked,
  onChange,
  value,
  size,
  hasRadioCheckmarks,
  icon,
  iconSize,
  image,
  radioCheckmarksCheckedIcon,
  radioCheckmarksUncheckedIcon,
  labelClassName = "",
  contentWrapperClassName = "",
  iconWrapperClassName = "",
  checkedOptionClassName = "",
  uncheckedOptionClassName = "",
  hasCheckedIcon = true,
  disabled = false,
  ...rest
}: RadioButtonProps) => {
  const hasIcon = icon && iconSize;

  return (
    <label
      htmlFor={id}
      {...rest}
      className={classNames(
        "m-0 flex w-full items-center justify-between border",
        {
          "bg-selector-active border-secondary text-selector-active drop-shadow":
            isChecked,
          "bg-primary border-selector-inactive text-default":
            !isChecked && !disabled,
          "rounded-lg px-3.5 py-2.5": size === "sm",
          "rounded-xl px-5 py-4": size === "md",
          [checkedOptionClassName]: isChecked && !disabled,
          [uncheckedOptionClassName]: !isChecked && !disabled,
          "bg-primary border-disabled text-disabled cursor-not-allowed":
            disabled,
        },
        labelClassName,
      )}
    >
      <div
        className={classNames(
          "mb-0 w-full",
          {
            "flex-1": !hasIcon,
            "flex items-center gap-3": hasIcon,
          },
          contentWrapperClassName,
        )}
      >
        {image}
        {hasIcon && (
          <span className={iconWrapperClassName}>
            <Icon
              type={icon}
              size={iconSize}
              className={classNames({
                "!h-4 !w-4": iconSize === 16,
                "!h-5 !w-5": iconSize === 20,
                "text-darkest": isChecked,
              })}
            />
          </span>
        )}
        {typeof label === "string" ? (
          <Typography.LabelSm weight="medium">
            <span
              className={classNames(
                {
                  "text-selector-active": isChecked,
                  "text-default": !isChecked,
                },
                hasCheckedIcon && image
                  ? "flex justify-center items-center gap-2 py-1"
                  : "",
              )}
            >
              {hasCheckedIcon && image && (
                <RadioButtonIcon
                  isChecked={isChecked}
                  hasRadioCheckmarks={hasRadioCheckmarks}
                  radioCheckmarksCheckedIcon={radioCheckmarksCheckedIcon}
                  radioCheckmarksUncheckedIcon={radioCheckmarksUncheckedIcon}
                />
              )}
              {label}
            </span>
          </Typography.LabelSm>
        ) : (
          label
        )}
      </div>
      {hasCheckedIcon && !image && (
        <RadioButtonIcon
          isChecked={isChecked}
          hasRadioCheckmarks={hasRadioCheckmarks}
        />
      )}
      <input
        className="hidden"
        type="radio"
        name={name}
        id={id}
        onChange={event => onChange(event.target.checked ? value : null)}
        disabled={disabled}
      />
    </label>
  );
};
